import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {PeoService} from '../../../../service/peo.service';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {PeoApiModel} from '../../../../model/peo-api.model';
import {DatePipe, NgIf} from '@angular/common';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {MatIconAnchor, MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatFormField, MatSuffix} from '@angular/material/form-field';
import {MatInput, MatLabel} from '@angular/material/input';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {FormsModule} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {
  PeoApiCredentialsAdminDialogComponent
} from '../../../dialog/admin/peo-api-credentials/peo-api-credentials.component';
import {RouterLink} from '@angular/router';
import {PeoAdminDialogComponent} from '../../../dialog/admin/peo/peo.component';
import {MatSnackBar, MatSnackBarRef, TextOnlySnackBar} from '@angular/material/snack-bar';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {UtilityService} from '../../../../service/utility.service';
import {PeoApiCredentialModel} from '../../../../model/peo-api-credential.model';

/**
 * @class
 * @component
 * @description This class maintains the Administrator PEO page component of our application.
 * @export
 * @name PeoAdminPageComponent
 */
@Component({
  imports: [
    MatCard,
    MatCardAvatar,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatIcon,
    DatePipe,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterCell,
    MatFooterRow,
    MatFooterRowDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIconButton,
    MatLabel,
    MatRow,
    MatRowDef,
    MatTable,
    MatTooltip,
    MatHeaderCellDef,
    MatFooterCellDef,
    MatNoDataRow,
    MatFormField,
    MatInput,
    MatSort,
    MatSortHeader,
    MatPaginator,
    NgIf,
    FormsModule,
    MatSuffix,
    MatIconAnchor,
    RouterLink,
    MatProgressSpinner
  ],
  selector: 'app-page-admin-peo',
  standalone: true,
  styleUrl: './peo.component.scss',
  templateUrl: './peo.component.html'
})
export class PeoAdminPageComponent implements AfterViewInit {

  /**
   * @description This property contains the filter text for the PEOs table.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#filterInput
   * @property
   * @protected
   * @type {string}
   */
  protected filterInput: string = UtilityService.EmptyString;

  /**
   * @description This property contains the columns to filter on the PEOs table.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#filterColumns
   * @property
   * @protected
   * @readonly
   * @type {(keyof PeoApiModel)[]}
   */
  protected readonly filterColumns: (keyof PeoApiModel)[] = ['peoId', 'name'];

  /**
   * @description This property contains the loading flag for the PEOs table.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#loading
   * @property
   * @protected
   * @type {boolean}
   */
  protected loading: boolean = false;

  /**
   * @description This property contains the PEOs registered in the system.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#peoDataSource
   * @property
   * @protected
   * @type {MatTableDataSource<PeoApiModel>}
   */
  protected peoDataSource: MatTableDataSource<PeoApiModel> = new MatTableDataSource<PeoApiModel>();

  /**
   * @description This property contains the paginator for the PEOs table.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#paginator
   * @property
   * @protected
   * @type {MatPaginator}
   */
  @ViewChild(MatPaginator)
  protected paginator!: MatPaginator;

  /**
   * @description This property contains the sorter for the PEOs table.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#sort
   * @property
   * @protected
   * @type {MatSort}
   */
  @ViewChild(MatSort)
  protected sort!: MatSort;

  /**
   * @description This property contains the columns for the PEOs table.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#tableColumns
   * @property
   * @protected
   * @type {string[]}
   */
  protected tableColumns: string[] = ['id', 'name', 'created', 'modified', '_actions'];

  /**
   * @constructor
   * @description This method instantiates a new instance of our peo page component with injected dependencies.
   * @param {MatDialog} dialog The injected instance of the Angular Material dialog service.
   * @param {PeoService} service The injected instance of our PrismHR PEO service provider.
   * @param {MatSnackBar} snackbar The injected instance of the Angular Material snackbar service.
   * @memberof PeoAdminPageComponent
   * @name PeoAdminPageComponent#constructor()
   * @public
   */
  public constructor(
    /**
     * @description This property contains the Angular Material dialog service.
     * @memberof PeoAdminPageComponent
     * @name PeoAdminPageComponent#dialog
     * @private
     * @property
     * @readonly
     * @type {MatDialog}
     */
    private readonly dialog: MatDialog,
    /**
     * @description This property contains the injected instance of our PrismHR Employee service provider.
     * @memberof PeoAdminPageComponent
     * @name PeoAdminPageComponent#service
     * @private
     * @property
     * @readonly
     * @type {PeoService}
     */
    private readonly service: PeoService,
    /**
     * @description This property contains the Angular Material snackbar service.
     * @memberof PeoAdminPageComponent
     * @name PeoAdminPageComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar
  ) {

    // Add our custom table filter predicate.
    this.peoDataSource.filterPredicate = UtilityService.TableMultiFilterPredicate<PeoApiModel>(this.filterColumns);
  }

  /**
   * @description This method loads the PEO details.
   * @memberof PeoAdminPageComponent
   * @method
   * @name PeoAdminPageComponent#loadPeoList()
   * @private
   * @returns {void}
   */
  private loadPeoList(): void {

    // Reset the loading flag.
    this.loading = true;

    // Reset the data on the PEOs table.
    this.peoDataSource.data = [];

    // Load the PEO details from the .NET backend API.
    this.service.list().subscribe({

      // Handle the completion event.
      complete: (): boolean => this.loading = false,

      // Handle any errors that occur.
      error: (e: Error): MatSnackBarRef<TextOnlySnackBar> => this.snackbar.open(`Failed to List PEOs with:  ${e.message}`, 'Dismiss', {
        duration: 5000,
        horizontalPosition: 'end',
        panelClass: ['snackbar-error'],
        verticalPosition: 'bottom'
      }),

      // Handle the next event.
      next: (p: PeoApiModel[]): void => {

        // Reset the list of PEOs.
        this.peoDataSource.data = p;

        // Set the paginator for the PEOs table.
        this.peoDataSource.paginator = this.paginator;

        // Set the sorter for the PEOs table.
        this.peoDataSource.sort = this.sort;

        // Reset the loading flag.
        this.loading = false;
      }
    });
  }

  /**
   * @description This method clears the filter on the PEOs table.
   * @memberof PeoAdminPageComponent
   * @method
   * @name PeoAdminPageComponent#clearFilter()
   * @protected
   * @returns {void}
   */
  protected clearFilter(): void {

    // Clear the filter input.
    this.filterInput = UtilityService.EmptyString;

    // Clear the filter on the PEOs in the instance.
    this.peoDataSource.filter = UtilityService.EmptyString;

    // Check for a paginator, then navigate to the first page.
    if (this.peoDataSource.paginator) this.peoDataSource.paginator.firstPage();
  }

  /**
   * @description This method filters the PEOs in the table.
   * @memberof PeoAdminPageComponent
   * @method
   * @name PeoAdminPageComponent#filterTable()
   * @param {Event} event The event that triggered the filter.
   * @protected
   * @returns {void}
   */
  protected filterTable(event: Event): void {

    // Reset the filter input.
    this.filterInput = (event.target as HTMLInputElement).value ?? UtilityService.EmptyString;

    // Ensure we have a filter input.
    if (!this.filterInput) return this.clearFilter();

    // Localize the columnar filter value.
    const filterValue: { [key: string]: string } = {};

    // Iterate over the filter columns and add the filter value to the filter input.
    this.filterColumns.forEach((c: string) => filterValue[c] = this.filterInput.trim().toLowerCase());

    // Apply the filter to the data table in the instance.
    this.peoDataSource.filter = JSON.stringify(filterValue);

    // Check for a paginator, then navigate to the first page.
    if (this.peoDataSource.paginator) this.peoDataSource.paginator.firstPage();
  }

  /**
   * @description This method manages the API credentials for a PEO.
   * @memberof PeoAdminPageComponent
   * @method
   * @name PeoAdminPageComponent#manageCredentials()
   * @param {string} peoId The ID of the PEO to manage.
   * @protected
   * @returns {void}
   */
  protected manageCredentials(peoId: string): void {

    // Open the dialog for the PEO API credentials.
    this.dialog.open(PeoApiCredentialsAdminDialogComponent, {

      // Pass the data to the dialog.
      data: {peoId },

      // Disable the backdrop click.
      disableClose: true,

      // Set the width of the dialog.
      width: '800px'
    }).afterClosed().subscribe((c?: PeoApiCredentialModel): void => {

      // Reload the PEO list when the dialog is closed and a PEO Credential is returned.
      if (!!c) this.loadPeoList()
    });
  }

  /**
   * @description This method manages PEO information in persistent storage.
   * @memberof PeoAdminPageComponent
   * @method
   * @name PeoAdminPageComponent#modifyPeo()
   * @param {PeoApiModel?} [peo] The PEO to modify.
   * @protected
   * @returns {void}
   */
  protected modifyPeo(peo?: PeoApiModel): void {

    // Open the dialog for the PEO.
    this.dialog.open(PeoAdminDialogComponent, {

      // Pass the data to the dialog.
      data: { peo },

      // Disable the backdrop click.
      disableClose: true,

      // Set the width of the dialog.
      width: '800px'
    }).afterClosed().subscribe((p?: PeoApiModel): void => {

      // Reload the PEO list when the dialog is closed and a PEO is returned.
      if (!!p) this.loadPeoList();
    });
  }

  /**
   * @description This method initializes the component.
   * @memberof PeoAdminPageComponent
   * @method
   * @name PeoAdminPageComponent#ngAfterViewInit()
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Load the PEO list.
    this.loadPeoList();
  }
}
